/**
 * Funções puras da aba Meios de Pagamento (Relatórios › Financeiro).
 * Regras em ERPApi/docs/funcionalidades/financeiro/relatorios-financeiros-e-dre.md (RN-DRE-33+).
 */
import type {
  PaymentMethodsBrandRow,
  PaymentMethodsMonth,
  PaymentMethodsTotals,
} from '@/types/financialReports';

/** Faixas na ordem da pilha, com as mesmas cores do PDF (paymentMethodsSeries no back). */
export const PAYMENT_METHOD_SERIES: { key: 'card1x' | 'card2to6x' | 'card7to12x' | 'pix'; label: string; color: string }[] = [
  { key: 'card1x', label: 'Cartão à vista', color: '#2563eb' },
  { key: 'card2to6x', label: 'Cartão 2 a 6x', color: '#7c3aed' },
  { key: 'card7to12x', label: 'Cartão 7 a 12x', color: '#f59e0b' },
  { key: 'pix', label: 'PIX', color: '#0d9488' },
];

const MONTH_ABBR = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "2026-03" → "mar/26". */
export const paymentMonthLabel = (month: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  return `${MONTH_ABBR[Number(m[2]) - 1]}/${m[1].slice(2)}`;
};

/** "AAAA-MM" de um ano e mês. */
export const toYearMonth = (year: number, month: number): string => `${year}-${String(month).padStart(2, '0')}`;

/** Período padrão: os últimos 12 meses, até o mês corrente. */
export const defaultPaymentPeriod = (today: Date = new Date()) => {
  const end = new Date(today.getFullYear(), today.getMonth(), 1);
  const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
  return {
    startMonth: start.getMonth() + 1,
    startYear: start.getFullYear(),
    endMonth: end.getMonth() + 1,
    endYear: end.getFullYear(),
  };
};

const EMPTY_TOTALS: PaymentMethodsTotals = { card1x: 0, card2to6x: 0, card7to12x: 0, pix: 0, other: 0, total: 0, orders: 0 };

/** Totais do mês para o recorte: consolidado (companyId null) ou uma empresa. */
export const monthTotalsFor = (m: PaymentMethodsMonth, companyId: number | null): PaymentMethodsTotals =>
  companyId == null ? m.totals : m.companies.find((c) => c.companyId === companyId)?.totals ?? EMPTY_TOTALS;

/** Linhas do gráfico empilhado: uma por mês, uma chave por faixa. */
export const buildPaymentChartData = (months: PaymentMethodsMonth[], companyId: number | null = null) =>
  months.map((m) => {
    const t = monthTotalsFor(m, companyId);
    return { label: paymentMonthLabel(m.month), card1x: t.card1x, card2to6x: t.card2to6x, card7to12x: t.card7to12x, pix: t.pix };
  });

/** Participação de uma parte no total, em % (null sem total). */
export const sharePct = (part: number, total: number): number | null => (total > 0 ? (part / total) * 100 : null);

/**
 * Colunas de parcelas da tabela de bandeiras: as que têm ao menos um pedido,
 * sempre com 1x (à vista). Devolve o número de parcelas (1..12).
 */
export const brandInstallmentColumns = (totals: PaymentMethodsBrandRow | null | undefined): number[] => {
  const cols = [1];
  if (!totals) return cols;
  for (let i = 1; i < 12; i++) {
    if ((totals.counts[i] ?? 0) > 0) cols.push(i + 1);
  }
  return cols;
};
